import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import { BlockButton } from '@/components/BlockButton';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  jobChangeProcessStatusNextStepSchema,
  type JobChangeProcessStatusNextStepSchema,
} from '../../schemas/jobChangeProcessStatusNextStepSchema';
import { NEXT_STEPS } from '../../constant';
import { Radio } from '@/components/Radio';
import { type ChangeEvent } from 'react';
import usePostJobChangeProcess from '../../api/usePostJobChangeProcess';
import type { JobChangeProcess } from '../../types/JobChangeProcess';
import { showSuccessToast, showErrorToast } from '../../lib/toast';
import DatePickerModal from '@/components/DatePickerModal';
import clsx from 'clsx';
import { datetimeFormat } from '@/utils/dateFormat';
import { useSetAtom } from 'jotai';
import { currentSelectjobChangeProcessAtom } from '../../atoms/jobChangeProcess';

type Props = {
  jobChangeProcess: JobChangeProcess;
  close: () => void;
  jobchangeProcessesRefetch: () => Promise<void>;
};

const keyMap: Record<string, string> = {
  is_able_to_next_interview: 'interview',
  is_able_to_final_interview: 'final_interview',
  is_able_to_meeting: 'meeting',
  is_able_to_offer_meeting: 'offer_meeting',
  is_able_to_reference_check: 'reference_check',
  is_able_to_spi: 'spi',
};

export default function StatusNextStepModalContents({
  jobChangeProcess,
  close,
  jobchangeProcessesRefetch,
}: Props) {
  const setCurrentSelectjobChangeProcess = useSetAtom(currentSelectjobChangeProcessAtom);
  const { mutateAsync } = usePostJobChangeProcess();
  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    control,
    watch,
  } = useForm<JobChangeProcessStatusNextStepSchema>({
    resolver: zodResolver(jobChangeProcessStatusNextStepSchema),
    mode: 'all',
    defaultValues: {
      status: '',
      date: undefined,
    },
  });

  const status = watch('status');
  const date = watch('date');

  const candidateName = jobChangeProcess.candidate_profile?.name ?? '';
  const companyName = jobChangeProcess.company?.name ?? '';

  const onSubmit: SubmitHandler<JobChangeProcessStatusNextStepSchema> = async (data) => {
    try {
      await mutateAsync({
        jobChangeProcessId: jobChangeProcess!.id,
        status: data.status,
        interviewed_at: data.date,
      });
      showSuccessToast('ステータスを変更しました');
    } catch (error) {
      showErrorToast('ステータスの変更に失敗しました');
    } finally {
      await jobchangeProcessesRefetch();
      setCurrentSelectjobChangeProcess(null);
      close();
    }
  };

  const onChangeStep = (e: ChangeEvent<HTMLInputElement>) => {
    setValue('status', e.currentTarget.value);
  };

  const onChangeDate = (value: string) => {
    setValue('date', value ? new Date(value) : undefined);
  };

  const onClear = () => {
    setValue('date', undefined);
    setValue('status', '');
    close();
  };

  const filteredSteps = NEXT_STEPS.map((step) => {
    if (step.value === 'interview') {
      const stepNumber = jobChangeProcess.next_progresses.next_interview_step;
      return { ...step, label: `${stepNumber}次面接` };
    }
    return step;
  }).filter((step) =>
    Object.entries(keyMap).some(([key, value]) => {
      const isEnabled =
        jobChangeProcess.next_progresses[key as keyof typeof jobChangeProcess.next_progresses];
      return isEnabled && value === step.value;
    })
  );

  return (
    <form className="w-[680px] bg-white" action={''} onSubmit={handleSubmit(onSubmit)}>
      <div className="px-6 py-4">
        <p className="text-base font-bold">
          {candidateName} × {companyName}
        </p>
      </div>
      <div className="border-y border-solid border-border p-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <p className="text-base font-bold">次回の選考を選択してください</p>
            <span className="rounded-full bg-danger_lighten px-3 pt-1 text-xs font-bold text-danger">
              必須
            </span>
          </div>
          <div className="mt-2 flex gap-6">
            {filteredSteps.map((m, key) => {
              return (
                <Controller
                  key={key}
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Radio
                      {...field}
                      value={m.value}
                      checked={status === m.value}
                      onChange={onChangeStep}
                    >
                      {m.label}
                    </Radio>
                  )}
                />
              );
            })}
          </div>
        </div>
        {status !== 'reference_check' && status !== 'spi' && (
          <div className="mt-6 flex flex-col gap-3">
            <p className="text-base font-bold">日時を選択してください</p>
            <div className="w-[236px]">
              <DatePickerModal
                value={date ? date.toISOString() : ''}
                onChange={onChangeDate}
                type="datetime"
              >
                <button
                  type="button"
                  className="flex h-[42px] w-[236px] items-center rounded-sm border border-solid border-border bg-white px-3 focus:border-2 focus:border-primary focus-visible:outline-hidden"
                >
                  <p className={clsx('text-base', !date && 'text-[#999999]')}>
                    {date ? datetimeFormat(date) : 'YYYY/MM/DD 00:00'}
                  </p>
                  <img
                    className="ml-auto"
                    src="/agent/job_change_processes/icon-schedule.svg"
                    alt=""
                    width={20}
                    height={20}
                  />
                </button>
              </DatePickerModal>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-4 px-6 py-4">
        <div className="w-[122px]">
          <BlockButton
            colorType="white"
            type="button"
            text="キャンセル"
            onClick={onClear}
            disabled={isSubmitting}
          />
        </div>
        <div className="w-[52px]">
          <BlockButton type="submit" text="決定" disabled={isSubmitting || status === ''} />
        </div>
      </div>
    </form>
  );
}
